import { Polyglot } from 'components/translate';
import HttpCodes from 'configs/HttpCodes';
import { action, observable } from 'mobx';
import { DatahubEventsService } from 'modules/app/services';
import {
  TryDeleteSubtitlesSchema,
  TryGenerateSubtitlesSchema,
  TryTranslateSubtitlesSchema,
  TryUploadSubtitlesSchema
} from 'modules/app/services/datahubEvents/media';
import { generateId } from 'modules/content/helper';
import { UiService } from 'modules/ui';
import { ALERT } from 'modules/ui/constants';

import {
  CHANGE_TIME,
  DEFAULT_CUE_DURATION,
  MIN_CUE_DURATION,
  SUBTITLE_STATUS,
  ALLOWED_EXTENSIONS,
  ERROR_SUBTITLE_MIME,
  PADDING_TIME_MOVE_SECONDS,
  ERROR_NO_CUES
} from './constants';
import SubtitleService from './SubtitleService';
import SubtitleStore from './SubtitleStore';

class SubtitleActions {
  errors = {
    invalid_format: { message: 'subtitle_invalid_file' },
    invalid_file: { message: 'subtitle_bad_file' },
    system_exception: { message: 'subtitle_unknown_error.upload' },
    timecode_badly_formated: { message: 'subtitle_line_error' }
  };

  setPlayer(player) {
    this.player = player;
  }

  @action
  async setSupportedTranscriptionLangs() {
    const response =
      await SubtitleService.fetchSubtitleSupportedTranscriptionLangs();

    SubtitleStore._supportedTranscriptionLangs = response;
  }

  @action
  async setSubtitleTrack(mediaCode, subtitleCode) {
    const response = await SubtitleService.fetchSubtitleTrack(
      mediaCode,
      subtitleCode
    );

    const cues = response.cues.map((cue) => {
      cue.id = generateId();
      return cue;
    });

    SubtitleStore.languageCode = response.language;
    SubtitleStore.cues = cues;
    SubtitleStore.duration = response.duration;
    SubtitleStore.status = response.status;
  }

  @action
  async setSubtitleList(mediaCode) {
    const subtitleList = await SubtitleService.fetchSubtitleList(mediaCode);
    const subtitles = { ...SubtitleStore.subtitles, [mediaCode]: subtitleList };

    SubtitleStore.subtitles = observable(subtitles);
  }

  @action
  setCurrentCue = (currentCue) => {
    currentCue = SubtitleService.cueToPrecision(currentCue);
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.player?.setTime(currentCue.startTime + 0.01);

    SubtitleStore.currentCue = currentCue;
  };

  setCurrentCueMoveSeconds = (currentCue) => {
    this.setCurrentCue(currentCue);
    this.moveSeconds(currentCue.startTime - PADDING_TIME_MOVE_SECONDS);
  };

  @action
  setNextCue = () => {
    const { currentCue } = SubtitleStore;

    const defaultNextCue = SubtitleStore.cues[0];
    const currentCueExists = Boolean(currentCue.id);

    const next = currentCueExists
      ? SubtitleService.getNextCue(currentCue.startTime, currentCue.id)
      : defaultNextCue;

    next.id && this.setCurrentCueMoveSeconds(next);
  };

  @action
  setPrevCue = () => {
    const { currentCue } = SubtitleStore;

    const prev = SubtitleService.getPrevCue(currentCue.endTime, currentCue.id);

    prev.id && this.setCurrentCueMoveSeconds(prev);
  };

  @action
  setScrollPosition(position) {
    position = position < 0 ? 0 : position;

    SubtitleStore.scrollPosition = position;
  }

  @action
  resetSubtitleCues() {
    SubtitleStore.cues = [];
  }

  @action
  resetCurrentCue = () => {
    SubtitleStore.currentCue = {};
  };

  addCue = (startTime) => {
    const endTime = DEFAULT_CUE_DURATION + startTime;
    const newCue = { startTime, endTime, id: generateId() };

    newCue.content = Polyglot.t('player_subtitles.new_subtitle_cue');

    const overridingCue = SubtitleService.getOverridingCue(startTime);

    if (overridingCue) {
      const minDurationDiff = startTime - overridingCue.startTime;
      const endDiff = overridingCue.endTime - startTime;

      const isAddEnabled =
        minDurationDiff > MIN_CUE_DURATION && endDiff > MIN_CUE_DURATION;

      isAddEnabled
        ? this.addOverridingCue(overridingCue, newCue)
        : this.notEnoughRemainingTime();
    } else if (!overridingCue) {
      this.pushCue(newCue);
    }
  };

  notEnoughRemainingTime() {
    UiService.setAlertError('cue_not_enough_remaining_time');
  }

  addOverridingCue(overridingCue, newCue) {
    overridingCue.endTime = newCue.startTime;

    this.modifyCue(overridingCue);
    this.pushCue(newCue);
  }

  pushCue(newCue) {
    const { cues } = SubtitleStore;

    SubtitleService.cueTimeCheck(newCue);

    const hasMinDuration = newCue.endTime - newCue.startTime > MIN_CUE_DURATION;

    if (!hasMinDuration) {
      return;
    }

    this.player?.addCue(newCue);

    const newCueIndex = cues.findIndex(
      (cue) => cue.endTime >= newCue.startTime
    );

    if (newCueIndex >= 0) {
      cues.splice(newCueIndex, 0, newCue);
    } else {
      cues.push(newCue);
    }

    SubtitleStore.cues = cues;
    this.setCurrentCue(newCue);
  }

  @action
  removeCue = (cue) => {
    let { cues } = SubtitleStore;

    cues = cues.filter(
      (oldCue) =>
        !(cue.endTime === oldCue.endTime || cue.startTime === oldCue.startTime)
    );

    cue = SubtitleService.cueToPrecision(cue);
    this.player?.removeCue(cue);

    SubtitleStore.cues = cues;
  };

  @action
  modifyCue = (newCue) => {
    const { cues } = SubtitleStore;

    const oldCueIndex = cues.findIndex((cue) => cue.id === newCue.id);
    let oldCue = cues.find((cue) => cue.id === newCue.id);

    oldCue = oldCue ? SubtitleService.cueToPrecision(oldCue) : newCue;

    cues[oldCueIndex] = newCue;

    SubtitleStore.cues = cues;
    this.setCurrentCue(newCue);

    if (SubtitleService.isCueDifferent(oldCue, newCue)) {
      this.player?.removeCue(oldCue);
      this.player?.addCue(newCue);
    }
  };

  resizeCue = (newTime, cue, type) => {
    const isEndChanged = type === CHANGE_TIME.END && newTime > cue.startTime;
    const isStartChanged = type === CHANGE_TIME.START && newTime < cue.endTime;

    if (isEndChanged) {
      cue.endTime = newTime;
    } else if (isStartChanged) {
      cue.startTime = newTime;
    }

    cue = SubtitleService.cueTimeCheck(cue);

    this.modifyCue(cue);
  };

  moveCue = (cue, initialCueDuration) => {
    const newCue = SubtitleService.cueTimeCheck({ ...cue });

    if (newCue.endTime !== cue.endTime) {
      newCue.startTime =
        newCue.startTime === 0
          ? initialCueDuration
          : newCue.endTime - initialCueDuration;
    }

    if (newCue.startTime !== cue.startTime) {
      newCue.endTime = newCue.startTime + initialCueDuration;
    }

    this.modifyCue(newCue);
  };

  @action
  setZoom(zoom) {
    SubtitleStore.zoom = parseFloat(zoom.toPrecision(1));
  }

  increaseZoom = () => {
    const { zoom } = SubtitleStore;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const newZoom = zoom === 1 ? zoom : zoom + 0.1;

    this.setZoom(newZoom);
  };

  decreaseZoom = () => {
    const { zoom } = SubtitleStore;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    const newZoom = zoom === 0.1 ? zoom : zoom - 0.1;

    this.setZoom(newZoom);
  };

  @action
  async removeSubtitle(mediaCode, subtitleCode) {
    SubtitleStore.subtitles[mediaCode] = SubtitleStore.subtitles[
      mediaCode
    ].filter((subtitle) => subtitle.id !== subtitleCode);

    let statusCode = HttpCodes.OK;
    let errorMessage = 'NOT_FAILURE_RESULT_EVENT';

    try {
      SubtitleService.removeSubtitle(mediaCode, subtitleCode);
    } catch (error) {
      statusCode = error.status;
      errorMessage = error.message;
    }

    DatahubEventsService.sendEvent({
      schema: new TryDeleteSubtitlesSchema({
        statusCode,
        failureDescription: errorMessage,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  }

  @action
  async uploadSubtitle(mediaCode, languageCode, subtitle) {
    let statusCode = HttpCodes.OK;
    let errorMessage = 'NOT_FAILURE_RESULT_EVENT';

    try {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const extension = subtitle.name.slice(-3);
      const hasMimeType = ALLOWED_EXTENSIONS.includes(extension);

      if (!hasMimeType) {
        throw ERROR_SUBTITLE_MIME;
      }

      const newSubtitle = await SubtitleService.uploadSubtitle(
        mediaCode,
        languageCode,
        subtitle
      );

      SubtitleStore.subtitles[mediaCode].push(newSubtitle);
      UiService.setAlertInfo('subtitle_upload', {}, ALERT.TIMEOUT.LONG);
    } catch (error) {
      statusCode = error.status;
      errorMessage = error.message;

      const SUBTITLE_ERROR_TIMEOUT = 10000;
      const { data } = error || {};
      const { messageCode, line } = data || {};
      const message = this.errors[messageCode]
        ? this.errors[messageCode].message
        : this.errors.system_exception.message;

      UiService.setAlertError(message, { line }, SUBTITLE_ERROR_TIMEOUT);
    }

    DatahubEventsService.sendEvent({
      schema: new TryUploadSubtitlesSchema({
        statusCode,
        failureDescription: errorMessage,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  }

  validateSubtitle() {
    const emptyCues = (SubtitleStore.cues?.length || 0) === 0;

    if (emptyCues) {
      throw new Error(ERROR_NO_CUES);
    }
  }

  saveSubtitle(mediaCode, subtitleCode) {
    return SubtitleService.saveSubtitle(mediaCode, subtitleCode);
  }

  async updateSubtitle(mediaCode, subtitleCode) {
    await this.saveSubtitle(mediaCode, subtitleCode);

    UiService.setAlertSuccess('subtitle_saved');
  }

  @action
  async transcribeMedia(mediaCode) {
    let statusCode = HttpCodes.OK;
    let errorMessage = 'NOT_FAILURE_RESULT_EVENT';
    let language = '';

    try {
      const response = await SubtitleService.transcribeMedia(mediaCode);

      language = response.languageCode;

      UiService.setAlertInfo('subtitle_create', {}, ALERT.TIMEOUT.LONG);

      await this.setSubtitleList(mediaCode);
    } catch (error) {
      const isAudioless = error.status === HttpCodes.UNPROCESSABLE_ENTITY;
      const alertMessage = isAudioless
        ? 'transcribe_no_content'
        : 'error_try_again';

      UiService.setAlertError(alertMessage, {}, ALERT.TIMEOUT.LONG);
      statusCode = error.status;
      errorMessage = error.message;
    }

    DatahubEventsService.sendEvent({
      schema: new TryGenerateSubtitlesSchema({
        language,
        statusCode,
        failureDescription: errorMessage,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  }

  @action
  async translateSubtitles(subtitleCode, mediaCode, languageCodes) {
    let statusCode = HttpCodes.OK;
    let errorMessage = 'NOT_FAILURE_RESULT_EVENT';

    try {
      UiService.setAlertInfo('subtitle_translate', {}, ALERT.TIMEOUT.LONG);

      const translateRequests = languageCodes.map((locale) =>
        SubtitleService.translateSubtitle(subtitleCode, mediaCode, locale)
      );

      await Promise.all(translateRequests);
      this.setSubtitleList(mediaCode);
    } catch (error) {
      UiService.setAlertError('error_try_again');
      statusCode = error.status;
      errorMessage = error.message;
    }

    DatahubEventsService.sendEvent({
      schema: new TryTranslateSubtitlesSchema({
        statusCode,
        failureDescription: errorMessage,
        result: statusCode === HttpCodes.OK ? 'SUCCESSFUL' : 'FAILURE'
      })
    });
  }

  downloadSubtitle(mediaCode, subtitleCode) {
    const subtitle = SubtitleStore.subtitles[mediaCode].find(
      (subtitle) => subtitle.id === subtitleCode
    );

    window.open(subtitle.url);
  }

  @action
  async retrySubtitle(subtitleCode, mediaCode) {
    await SubtitleService.retrySubtitle(mediaCode, subtitleCode);

    this.setSubtitleList(mediaCode);
  }

  @action
  toggleSubtitle(currentStatus, mediaCode, subtitleCode) {
    const state = currentStatus === SUBTITLE_STATUS.ACTIVE;

    SubtitleService.toggleSubtitle(!state, mediaCode, subtitleCode);

    const subtitleIndex = SubtitleStore.subtitles[mediaCode].findIndex(
      (subtitle) => subtitle.id === subtitleCode
    );
    const newStatus =
      currentStatus === SUBTITLE_STATUS.ACTIVE
        ? SUBTITLE_STATUS.INACTIVE
        : SUBTITLE_STATUS.ACTIVE;

    if (SubtitleStore.subtitles[mediaCode].length > 0) {
      SubtitleStore.subtitles[mediaCode][subtitleIndex].status = newStatus;
    }

    SubtitleStore.status = newStatus;
  }

  @action
  movePixels(pixelsMoved, scrollWidth) {
    const percMoved = pixelsMoved / scrollWidth;
    const position = SubtitleStore.scrollPosition + percMoved;

    this.setScrollPosition(position);
  }

  @action
  moveSeconds(seconds) {
    const percMoved = seconds / SubtitleStore.duration;

    this.setScrollPosition(percMoved);
  }

  @action
  moveReferenceLine(pixelsMoved, scrollWidth, timelineWidth) {
    const percMoved = pixelsMoved / scrollWidth;

    let newPosition = SubtitleStore.referenceLinePosition + percMoved;

    newPosition = newPosition > 1 ? 1 : newPosition;
    newPosition = newPosition < 0 ? 0 : newPosition;

    const maxPositionDuration = timelineWidth / scrollWidth;

    newPosition =
      newPosition > maxPositionDuration ? maxPositionDuration : newPosition;

    SubtitleStore.referenceLinePosition = newPosition;
  }

  async approveSubtitle(mediaCode, subtitleCode) {
    this.saveSubtitle(mediaCode, subtitleCode);
    await SubtitleService.approveSubtitle(mediaCode, subtitleCode);
    this.setSubtitleList(mediaCode);

    UiService.setAlertSuccess('subtitle_approved');
  }
}

export default new SubtitleActions();
